import React, { useEffect, useMemo, useState, useContext } from "react";
import { Link, useSearchParams } from "react-router-dom";
import "./../../../assets/css/admin/gestionUtilisateur/AdminUsersPage.css";
import Breadcrumb from "../../../components/breadcrumb/Breadcrumb";
import useUtilisateur from "../../../hooks/useUtilisateur";
import defaultImage from "../../../assets/images/utilisateur/default.jpeg";
import LoaderAdminUsers from "../../../components/loader/admin/gestionUtilisateur/LoaderAdminUsers";
import { AuthContext } from "../../../contexts/AuthContext.js";

export default function AdminUsersPage({ setAlert }) {
  const { user:currentUser } = useContext(AuthContext);
  const { getAllUser, updateOneUser } = useUtilisateur();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const id = searchParams.get("id") || "";
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | blocked
  const [sort, setSort] = useState("newest");
  const [typeCompte, setTypeCompte] = useState("all"); // all | 1 | 2 | 3

  const [page, setPage] = useState(1);
  const PAGE_SIZE = 15;

  const [confirmModal, setConfirmModal] = useState({
    open: false,
    user: null,
  });

  const breadcrumbItems = id
    ? [
      { label: "Mon Compte", link: "/utilisateur/compte" },
      { label: "Signalements", link: "/admin/signalements" },
      { label: "Gestion Utilisateurs" },
    ]
    : [
      { label: "Mon Compte", link: "/utilisateur/compte" },
      { label: "Gestion Utilisateurs" },
    ];

  const normalized = (v) =>
    String(v ?? "")
      .toLowerCase()
      .trim();

  const formatDate = (d) => {
    if (!d) return "—";
    try {
      return new Date(d).toLocaleDateString("fr-FR");
    } catch {
      return "—";
    }
  };

  // --- Labels depuis ton modèle ---
  const roleLabel = (r) => {
    const n = Number(r);
    if (n === 1) return "Admin";
    if (n === 2) return "Vendeur/Acheteur";
    return "—";
  };

  const roleBadgeClass = (r) => {
    const n = Number(r);
    if (n === 1) return "badgeAdmin";
    return "badgeUser";
  };

  const providerLabel = (p) => {
    const n = Number(p);
    if (n === 1) return "Email/MDP";
    if (n === 2) return "Google";
    if (n === 3) return "Facebook";
    if (n === 4) return "Apple";
    return "—";
  };

  const typeCompteLabel = (t) => {
    const n = Number(t);
    if (n === 1) return "Personnel";
    if (n === 2) return "Professionnel";
    return "—";
  };

  const archiveLabel = (a) => {
    const n = Number(a);
    if (n === 1) return "Archivé";
    if (n === 2) return "Nonarchivé";
    return "—";
  };

  const statusConnexionLabel = (s) => {
    const n = Number(s);
    if (n === 1) return "Connecté";
    if (n === 2) return "Déconnecté";
    return "—";
  };

  // active = Number (1 actif / 2 désactivé) + support booléen legacy
  const isUserActive = (u) => {
    const a = u?.active;
    if (typeof a === "boolean") return a === true;
    const n = Number(a ?? 1);
    return n === 1;
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      let searchParams = {};
      if (id) {
        searchParams._id = id;
      }
      const res = await getAllUser({
        ...searchParams,
        limit: 10000,
      });
      const data = res?.data?.data || res?.data || [];
      setUsers(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setUsers([]);
      setAlert?.({
        type: "danger",
        message: "Impossible de récupérer la liste des utilisateurs.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    let list = [...(users || [])];

    if (q.trim()) {
      const query = normalized(q);
      list = list.filter((u) => {
        const fullName = `${u?.nom ?? ""} ${u?.prenom ?? ""}`.trim();
        return (
          normalized(fullName).includes(query) ||
          normalized(u?.email).includes(query) ||
          normalized(u?.tel).includes(query) ||
          normalized(u?.ville).includes(query) ||
          normalized(u?.adresse).includes(query)
        );
      });
    }

    if (role !== "all") {
      list = list.filter(
        (u) => String(Number(u?.role)) === String(Number(role)),
      );
    }

    if (typeCompte !== "all") {
      list = list.filter(
        (u) => String(Number(u?.typeCompte)) === String(Number(typeCompte)),
      );
    }

    if (statusFilter !== "all") {
      if (statusFilter === "active") list = list.filter((u) => isUserActive(u));
      if (statusFilter === "blocked")
        list = list.filter((u) => !isUserActive(u));
    }

    if (sort === "newest") {
      list.sort(
        (a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0),
      );
    } else if (sort === "oldest") {
      list.sort(
        (a, b) => new Date(a?.createdAt || 0) - new Date(b?.createdAt || 0),
      );
    } else if (sort === "name") {
      list.sort((a, b) => {
        const an = normalized(`${a?.nom ?? ""} ${a?.prenom ?? ""}`);
        const bn = normalized(`${b?.nom ?? ""} ${b?.prenom ?? ""}`);
        return an.localeCompare(bn);
      });
    }

    return list;
  }, [users, q, role, statusFilter, sort, typeCompte]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedUsers = filteredUsers.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  );

  useEffect(() => {
    setPage(1);
  }, [q, role, statusFilter, sort, typeCompte]);

  const handleToggleBlock = async (u) => {
    try {
      const id = u?._id;
      if (!id) return;

      const currentlyActive = isUserActive(u);
      const nextActiveValue = currentlyActive ? 2 : 1; // 1=actif, 2=désactivé

      await updateOneUser("", id, { active: nextActiveValue });

      setUsers((prev) =>
        (prev || []).map((x) =>
          String(x._id) === String(id) ? { ...x, active: nextActiveValue } : x,
        ),
      );

      setAlert?.({
        type: "success",
        message:
          nextActiveValue === 1
            ? "Utilisateur réactivé."
            : "Utilisateur bloqué.",
      });
    } catch (e) {
      console.error(e);
      setAlert?.({
        type: "danger",
        message: "Action impossible pour le moment.",
      });
    }
  };

  const imageBaseUrl = process.env.REACT_APP_ARTICLE_IMAGE;

  const resolveUserPhotoUrl = (photo) => {
    const p = String(photo || "").trim();
    if (!p || p === "/default.png") return defaultImage;
    if (/^https?:\/\//i.test(p)) return p;
    if (p.startsWith("/")) return `${imageBaseUrl}${p}`;
    return `${imageBaseUrl}/${p}`;
  };

  const openConfirmModal = (u) => {
    setConfirmModal({
      open: true,
      user: u,
    });
  };

  const closeConfirmModal = () => {
    setConfirmModal({
      open: false,
      user: null,
    });
  };

  const confirmToggleBlock = async () => {
    if (!confirmModal.user) return;

    await handleToggleBlock(confirmModal.user);
    closeConfirmModal();
  };

  return (
    <div className="adminUsersPage">
      <div className="container">
        <Breadcrumb items={breadcrumbItems} />

        <div className="adminUsersGrid">
          <section className="adminUsersCard adminUsersCardCenter">
            <div className="adminUsersHeader">
              <div className="adminUsersHeaderLeft">
                <div className="adminUsersTitle">Gestion des utilisateurs</div>
                <div className="adminUsersSubtitle">
                  {loading
                    ? "Chargement..."
                    : `${filteredUsers.length} utilisateur(s)`}
                </div>
              </div>

              <div className="adminUsersHeaderRight">
                <button
                  type="button"
                  className="adminUsersBtn adminUsersBtnSoft"
                  onClick={fetchUsers}
                  title="Rafraîchir"
                  disabled={loading}
                >
                  <i className="bi bi-arrow-clockwise" />
                  <span>Rafraîchir</span>
                </button>
              </div>
            </div>

            <div className="adminUsersFilters">
              <div className="adminUsersSearch">
                <i className="bi bi-search" aria-hidden="true" />
                <input
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Rechercher : nom, email, tel, ville, adresse..."
                />
              </div>

              <div className="adminUsersSelects">
                <select value={role} onChange={(e) => setRole(e.target.value)}>
                  <option value="all">Tous les rôles</option>
                  <option value="1">Admin</option>
                  <option value="2">Vendeur/Acheteur</option>
                  <option value="3">Lecture seule</option>
                </select>

                <select value={typeCompte} onChange={(e) => setTypeCompte(e.target.value)}>
                  <option value="all">Tous les comptes</option>
                  <option value="1">Particulier</option>
                  <option value="2">Professionnel</option>
                  <option value="3">Test</option>
                </select>

                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">Tous les statuts</option>
                  <option value="active">Actifs</option>
                  <option value="blocked">Bloqués</option>
                </select>

                <select value={sort} onChange={(e) => setSort(e.target.value)}>
                  <option value="newest">Plus récents</option>
                  <option value="oldest">Plus anciens</option>
                  <option value="name">Nom A → Z</option>
                </select>
              </div>
            </div>

            {loading ? (
              <div className="adminUsersSkeletonWrap">
                <LoaderAdminUsers style={{ width: "100%", height: "auto" }} />
              </div>
            ) : pagedUsers.length === 0 ? (
              <div className="adminUsersEmpty">
                <i className="bi bi-people" />
                <div>Aucun utilisateur trouvé.</div>
              </div>
            ) : (
              <div className="adminUsersTableWrap">
                <table className="adminUsersTable">
                  <thead>
                    <tr>
                      <th>Utilisateur</th>
                      <th className="hideOnMobile">Email</th>
                      <th className="hideOnMobile">Téléphone</th>
                      {/* <th className="hideOnMobile">Ville</th> */}

                      {/* <th className="hideOnMobile">Type</th> */}
                      <th className="hideOnMobile">Connexion</th>
                      <th className="hideOnMobile">Archive</th>
                      {/* <th className="hideOnMobile">Connexion</th> */}
                      {/* <th>Rôle</th> */}
                      {/* <th>Actif</th> */}
                      <th className="hideOnMobile">Créé</th>
                      {currentUser?.role == 1 && (
                        <th className="colActions">Actions</th>
                      )}

                    </tr>
                  </thead>

                  <tbody>
                    {pagedUsers.map((u) => {
                      const fullName =
                        `${u?.nom ?? ""} ${u?.prenom ?? ""}`.trim() || "—";
                      const active = isUserActive(u);
                      //  const photoUrl = resolvePhotoUrl(u?.photo);
                      const photoUrl = resolveUserPhotoUrl(u?.photo);

                      return (
                        <tr key={u?._id} className={u?.typeCompte == 3 ? "adminUserTest" : ""}>
                          <td>
                            <div className="adminUsersCellUser">
                              <div className="adminUsersAvatar">
                                <img
                                  src={photoUrl}
                                  alt={fullName}
                                  loading="lazy"
                                  onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = defaultImage;
                                  }}
                                />
                              </div>

                              <div className="adminUsersCellUserText">
                                <div className="adminUsersName">
                                  {fullName}
                                  <span className="adminUsersBadge badgeSoft">
                                    {typeCompteLabel(u?.typeCompte)}
                                  </span>
                                </div>

                                <div className="adminUsersMobileMeta">
                                  <div>
                                    <i className="bi bi-envelope" />{" "}
                                    {u?.email || "—"}
                                  </div>
                                  <div>
                                    <i className="bi bi-telephone" />{" "}
                                    {u?.tel || "—"}
                                  </div>
                                  {/* <div>
                                    <i className="bi bi-geo-alt" />{" "}
                                    {u?.ville || "—"}
                                  </div> */}
                                  <div>
                                    <i className="bi bi-house" />{" "}
                                    {u?.adresse || "—"}
                                  </div>

                                  {/* <div>
                                    <i className="bi bi-person-badge" />{" "}
                                    {roleLabel(u?.role)} ·{" "}
                                    {typeCompteLabel(u?.typeCompte)}
                                  </div> */}

                                  <div>
                                    <i className="bi bi-shield-lock" />{" "}
                                    {providerLabel(u?.authProvider)}
                                  </div>

                                  <div>
                                    <i className="bi bi-archive" />{" "}
                                    {archiveLabel(u?.archive)} ·{" "}
                                    <i className="bi bi-wifi" />{" "}
                                    {statusConnexionLabel(u?.status)}
                                  </div>

                                  <div>
                                    <i className="bi bi-calendar3" />{" "}
                                    {formatDate(u?.createdAt)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>

                          <td className="muted hideOnMobile">
                            {u?.email || "—"}
                          </td>
                          <td className="muted hideOnMobile">
                            {u?.tel || "—"}
                          </td>
                          {/* <td className="muted hideOnMobile">
                            {u?.ville || "—"}
                          </td> */}

                          {/* <td className="hideOnMobile">
                            <span className="adminUsersBadge badgeSoft">
                              {typeCompteLabel(u?.typeCompte)}
                            </span>
                          </td> */}

                          <td className="hideOnMobile muted">
                            {providerLabel(u?.authProvider)}
                          </td>

                          <td className="hideOnMobile">
                            <span
                              className={`adminUsersBadge ${Number(u?.archive) === 1
                                ? "badgeArchived"
                                : "badgeNotArchived"
                                }`}
                            >
                              {archiveLabel(u?.archive)}
                            </span>
                          </td>

                          {/* <td className="hideOnMobile">
                            <span className="adminUsersBadge badgeSoft">
                              {statusConnexionLabel(u?.status)}
                            </span>
                          </td>

                          <td>
                            <span
                              className={`adminUsersBadge ${roleBadgeClass(u?.role)}`}
                            >
                              {roleLabel(u?.role)}
                            </span>
                          </td>

                          <td>
                            <span
                              className={`adminUsersBadge ${active ? "badgeActive" : "badgeBlocked"}`}
                            >
                              {active ? "actif" : "bloqué"}
                            </span>
                          </td> */}

                          <td className="muted hideOnMobile">
                            {formatDate(u?.createdAt)}
                          </td>

                          {currentUser?.role == 1 && (
                            <td className="colActions">
                              <div className="adminUsersActions">
                                <Link
                                  to={`/utilisateur/profil?id=${u?._id}`}
                                  className="adminUsersIconBtn linkBtn"
                                  title="Modifier"
                                >
                                  <i className="bi bi-pencil-square" />
                                </Link>

                                <button
                                  type="button"
                                  className="adminUsersIconBtn"
                                  title={active ? "Bloquer" : "Réactiver"}
                                  onClick={() => openConfirmModal(u)}
                                >
                                  <i
                                    className={`bi ${active ? "bi-unlock user_open" : "bi-lock user_lock"}`}
                                  />
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {!loading && filteredUsers.length > 0 && (
              <div className="adminUsersPagination">
                <button
                  type="button"
                  className="adminUsersPageBtn"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                >
                  <i className="bi bi-chevron-left" />
                </button>

                <div className="adminUsersPageInfo">
                  Page <b>{safePage}</b> / {totalPages}
                </div>

                <button
                  type="button"
                  className="adminUsersPageBtn"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                >
                  <i className="bi bi-chevron-right" />
                </button>
              </div>
            )}
          </section>
        </div>
        {confirmModal.open && (
          <div className="adminUsersModalOverlay">
            <div className="adminUsersModal">
              <div className="adminUsersModalIcon">
                <i
                  className={`bi ${isUserActive(confirmModal.user)
                    ? "bi-exclamation-triangle"
                    : "bi-check-circle"
                    }`}
                />
              </div>

              <h3>
                {isUserActive(confirmModal.user)
                  ? "Bloquer cet utilisateur ?"
                  : "Réactiver cet utilisateur ?"}
              </h3>

              <p>
                {isUserActive(confirmModal.user)
                  ? "Cet utilisateur ne pourra plus utiliser son compte tant qu’il est bloqué."
                  : "Cet utilisateur pourra de nouveau utiliser son compte."}
              </p>

              <div className="adminUsersModalUser">
                {`${confirmModal.user?.nom ?? ""} ${confirmModal.user?.prenom ?? ""
                  }`.trim() || "Utilisateur"}
              </div>

              <div className="adminUsersModalActions">
                <button
                  type="button"
                  className="adminUsersModalCancel"
                  onClick={closeConfirmModal}
                >
                  Annuler
                </button>

                <button
                  type="button"
                  className={
                    isUserActive(confirmModal.user)
                      ? "adminUsersModalConfirm danger"
                      : "adminUsersModalConfirm success"
                  }
                  onClick={confirmToggleBlock}
                >
                  {isUserActive(confirmModal.user) ? "Bloquer" : "Réactiver"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
